import { useState, useEffect, useContext } from "react";
import { UserContext, API } from "@/App";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { 
  Crown, Gift, Zap, TrendingUp, Users, Star,
  ChevronRight, Sparkles, Trophy, Shield, Loader2
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { celebrateWin } from "@/lib/celebrations";

export const VIPPage = () => {
  const { user, refreshBalance, token } = useContext(UserContext);
  const [vipStatus, setVipStatus] = useState(null);
  const [dailyBonus, setDailyBonus] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [vipRes, bonusRes, achieveRes] = await Promise.all([
        axios.get(`${API}/vip/status`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/bonus/daily`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/achievements`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setVipStatus(vipRes.data);
      setDailyBonus(bonusRes.data);
      setAchievements(achieveRes.data.achievements || []);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const claimDailyBonus = async () => {
    setClaiming(true);
    try {
      const response = await axios.post(`${API}/bonus/claim-daily`, {}, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      celebrateWin(1, response.data.bonus);
      await refreshBalance();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to claim bonus");
    } finally {
      setClaiming(false);
    }
  };

  const claimRakeback = async () => {
    try {
      const response = await axios.post(`${API}/vip/claim-rakeback`, {}, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      celebrateWin(1, response.data.claimed);
      await refreshBalance();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to claim rakeback");
    }
  };

  const checkAchievements = async () => {
    try {
      const response = await axios.post(`${API}/achievements/check`, {}, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.newly_unlocked.length > 0) {
        toast.success(response.data.message);
        celebrateWin(2, response.data.total_reward);
        await refreshBalance();
        fetchData();
      } else {
        toast.info("No new achievements unlocked yet!");
      }
    } catch (error) {
      toast.error("Failed to check achievements");
    }
  };

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${h}h ${m}m`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-[#FFD600] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  const levelColors = {
    1: "#CD7F32", 2: "#C0C0C0", 3: "#FFD700", 
    4: "#E5E4E2", 5: "#B9F2FF", 6: "#FF007A"
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FFD600] to-[#FF6B00] flex items-center justify-center">
            <Crown className="w-6 h-6 text-black" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white font-['Rajdhani']">VIP Rewards</h1>
            <p className="text-[#A1A1AA] text-sm">Level up and earn exclusive rewards</p>
          </div>
        </div>

        {/* VIP Status Card */}
        <div className="p-6 rounded-2xl bg-gradient-to-br from-[#1A1B1F] to-[#0F0F10] border border-white/10">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Current Level */}
            <div className="flex-1">
              <div className="flex items-center gap-4 mb-4">
                <div 
                  className="w-20 h-20 rounded-2xl flex items-center justify-center text-3xl font-bold"
                  style={{ backgroundColor: `${vipStatus?.color}20`, color: vipStatus?.color }}
                >
                  {vipStatus?.level}
                </div>
                <div>
                  <p className="text-[#A1A1AA] text-sm">Current Level</p>
                  <h2 className="text-2xl font-bold" style={{ color: vipStatus?.color }}>
                    {vipStatus?.name}
                  </h2>
                  <p className="text-white/60 text-sm">
                    {vipStatus?.rakeback * 100}% Rakeback • ₹{vipStatus?.daily_bonus} Daily
                  </p>
                </div>
              </div>
              
              {vipStatus?.next_level && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#A1A1AA]">Progress to {vipStatus.next_level_name}</span>
                    <span className="text-white">{vipStatus.progress}%</span>
                  </div>
                  <Progress value={vipStatus.progress} className="h-3" />
                  <p className="text-[#52525B] text-xs">
                    Wager ₹{(vipStatus.next_level_wager - vipStatus.total_wagered).toLocaleString()} more
                  </p>
                </div>
              )}
            </div>

            {/* Rakeback */}
            <div className="w-full md:w-64 p-4 rounded-xl bg-white/5">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-5 h-5 text-[#00E701]" />
                <span className="text-white font-medium">Rakeback</span>
              </div>
              <p className="text-3xl font-bold text-[#00E701] font-mono mb-2">
                ₹{(user?.rakeback_balance || 0).toFixed(2)}
              </p>
              <Button
                onClick={claimRakeback}
                disabled={(user?.rakeback_balance || 0) < 10}
                className="w-full bg-[#00E701] hover:bg-[#00C201] text-black font-bold"
                size="sm"
              >
                Claim Rakeback
              </Button>
              <p className="text-[#52525B] text-xs mt-2">Min ₹10 to claim</p>
            </div>
          </div>
        </div>

        {/* Daily Bonus */}
        <div className="p-6 rounded-2xl bg-[#1A1B1F] border border-white/10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#FFD600]/20 flex items-center justify-center">
                <Gift className="w-6 h-6 text-[#FFD600]" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Daily Bonus</h3>
                <p className="text-[#A1A1AA] text-sm">
                  {dailyBonus?.can_claim 
                    ? "Ready to claim!" 
                    : `Next claim in ${formatTime(dailyBonus?.next_claim_in || 0)}`
                  }
                </p>
              </div>
            </div>
            
            <div className="text-right">
              <p className="text-2xl font-bold text-[#FFD600] font-mono">
                ₹{dailyBonus?.total_bonus}
              </p>
              <p className="text-[#A1A1AA] text-xs">
                Base ₹{dailyBonus?.bonus_amount} + Streak ₹{dailyBonus?.streak_bonus}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex-1 flex items-center gap-2">
              <Zap className="w-4 h-4 text-[#FF6B00]" />
              <span className="text-white">
                {dailyBonus?.current_streak} day streak
              </span>
            </div>
            <Button
              onClick={claimDailyBonus}
              disabled={!dailyBonus?.can_claim || claiming}
              className="bg-[#FFD600] hover:bg-[#E6C200] text-black font-bold px-6"
            >
              {claiming ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Gift className="w-4 h-4 mr-2" />
                  Claim ₹{dailyBonus?.total_bonus}
                </>
              )}
            </Button>
          </div>
        </div>

        {/* VIP Levels */}
        <div className="p-6 rounded-2xl bg-[#1A1B1F] border border-white/10">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-[#FFD600]" />
            VIP Levels
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {Object.entries(vipStatus?.all_levels || {}).map(([level, data]) => (
              <div 
                key={level}
                className={`p-4 rounded-xl border transition-all ${
                  parseInt(level) === vipStatus?.level
                    ? 'border-2 scale-105'
                    : 'border-white/10 opacity-60'
                }`}
                style={{ 
                  borderColor: parseInt(level) === vipStatus?.level ? data.color : undefined,
                  backgroundColor: `${data.color}10`
                }}
              >
                <div className="text-center">
                  <div 
                    className="text-2xl font-bold mb-1"
                    style={{ color: data.color }}
                  >
                    {level}
                  </div>
                  <p className="text-white text-sm font-medium">{data.name}</p>
                  <p className="text-[#A1A1AA] text-xs mt-1">
                    {data.rakeback * 100}% rakeback
                  </p>
                  <p className="text-[#A1A1AA] text-xs">
                    ₹{data.daily_bonus}/day
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Achievements */}
        <div className="p-6 rounded-2xl bg-[#1A1B1F] border border-white/10">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Trophy className="w-5 h-5 text-[#00E701]" />
              Achievements
            </h3>
            <Button onClick={checkAchievements} variant="outline" size="sm" className="border-white/20">
              <Sparkles className="w-4 h-4 mr-2" />
              Check Progress
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {achievements.map((achievement) => (
              <div 
                key={achievement.id}
                className={`p-4 rounded-xl border transition-all ${
                  achievement.unlocked
                    ? 'bg-[#00E701]/10 border-[#00E701]/30'
                    : 'bg-white/5 border-white/10 opacity-60'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    achievement.unlocked ? 'bg-[#00E701]/20' : 'bg-white/10'
                  }`}>
                    {achievement.unlocked ? (
                      <Trophy className="w-5 h-5 text-[#00E701]" />
                    ) : (
                      <Shield className="w-5 h-5 text-white/40" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium ${achievement.unlocked ? 'text-[#00E701]' : 'text-white'}`}>
                      {achievement.name}
                    </p>
                    <p className="text-[#A1A1AA] text-xs">{achievement.description}</p>
                    <p className="text-[#FFD600] text-xs mt-1">+₹{achievement.reward}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
};
